import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Warning, CalendarBlank, ArrowCircleRight } from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);

export default function UpcomingPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('60');

  useEffect(() => {
    setLoading(true);
    api.get('/payments/upcoming', { params: { days: parseInt(days) } })
      .then(r => setPayments(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  const overdue = payments.filter(p => p.is_overdue);
  const upcoming = payments.filter(p => !p.is_overdue);

  return (
    <div className="space-y-4" data-testid="upcoming-payments-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Upcoming Payments</h1>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[160px] rounded-sm h-9" data-testid="days-filter">
            <CalendarBlank size={14} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="14">Next 14 days</SelectItem>
            <SelectItem value="30">Next 30 days</SelectItem>
            <SelectItem value="60">Next 60 days</SelectItem>
            <SelectItem value="90">Next 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overdue */}
      {overdue.length > 0 && (
        <Card className="rounded-sm border border-[#EF4444]/30 bg-[#FEE2E2]/10">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Warning size={16} className="text-[#EF4444]" />Overdue ({overdue.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <PaymentTable items={overdue} isOverdue />
          </CardContent>
        </Card>
      )}

      {/* Upcoming */}
      <Card className="rounded-sm border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Upcoming ({upcoming.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[#94A3B8]">Loading...</div>
          ) : upcoming.length === 0 ? (
            <div className="p-8 text-center text-[#94A3B8]">No upcoming payments</div>
          ) : (
            <PaymentTable items={upcoming} />
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-[#94A3B8] italic">
        This view combines actual payment requests with recurring contract obligations. Items marked as "Recurring" are contractual obligations, not submitted payment requests.
      </p>
    </div>
  );
}

function PaymentTable({ items, isOverdue }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className={isOverdue ? 'bg-[#FEE2E2]/30' : 'bg-[#F1F5F9]'}>
          <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Type</TableHead>
          <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Reference</TableHead>
          <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Vendor</TableHead>
          <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold text-right">Amount</TableHead>
          <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Due Date</TableHead>
          <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map(p => (
          <TableRow key={`${p.type}-${p.id}`} className="hover:bg-[#F8FAFC]">
            <TableCell>
              <span className={`text-[10px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded-sm ${
                p.type === 'payment_request' ? 'bg-[#0055FF]/10 text-[#0055FF]' : 'bg-[#8B5CF6]/10 text-[#8B5CF6]'
              }`}>
                {p.type === 'payment_request' ? 'Request' : 'Recurring'}
              </span>
            </TableCell>
            <TableCell>
              <Link
                to={p.type === 'payment_request' ? `/payments/${p.id}` : `/contracts/${p.id}`}
                className="text-[#0055FF] hover:underline text-sm font-medium"
              >
                {p.reference}
              </Link>
            </TableCell>
            <TableCell className="text-sm">{p.vendor_name}</TableCell>
            <TableCell className="text-right text-sm font-medium">{fmt(p.amount)}</TableCell>
            <TableCell className={`text-sm ${isOverdue ? 'text-[#991B1B] font-medium' : ''}`}>{p.due_date}</TableCell>
            <TableCell className="text-sm text-[#475569]">{p.status}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
